import config from "console_config";
import logger from "Libs/logger";
import { request } from "Libs/platform";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { projectCreationActions } from "../projectCreation.reducer";
import {
  selectGitProviderError,
  selectGitProviderLoading,
  selectHasFetchedGithubOrganizations
} from "../projectCreation.selectors";

import type { GithubOrganization } from "../projectCreation.types";

const githubAppEndpoint = `${config.URL_API_PROXY}/gh`;

export type FetchGithubOrganizationsProps = {
  organizationId: string;

  /**
   * Bypasses the once-per-organization guard.
   *
   * Set only by the tab-return refetch, whose whole purpose is to pick up an
   * installation the user just made on github.com. Every other caller wants the
   * guard, so this defaults to off.
   */
  force?: boolean;
};

/**
 * Fetches GitHub organizations (installations) for an organization.
 *
 * This thunk fetches the list of GitHub App installations available
 * to the organization and stores them in Redux state.
 *
 * Three components mount into this flow and each of them needs the list, so the
 * "have we already asked?" decision lives in `condition` rather than in three
 * copies of the same guard. Callers dispatch unconditionally.
 */
export const fetchGithubOrganizations = createAppAsyncThunk(
  "onboarding/fetchGithubOrganizations",
  async (
    { organizationId }: FetchGithubOrganizationsProps,
    { dispatch, rejectWithValue }
  ) => {
    dispatch(
      projectCreationActions.setGitProviderLoading({
        organizationId,
        isLoading: true
      })
    );

    try {
      const organizations = (await request(
        `${githubAppEndpoint}/${organizationId}/orgs`
      )) as GithubOrganization[];

      dispatch(
        projectCreationActions.setGithubOrganizations({
          organizationId,
          organizations
        })
      );

      dispatch(
        projectCreationActions.setGitProviderLoading({
          organizationId,
          isLoading: false
        })
      );

      return { organizations };
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "An error occurred";

      logger(err, {
        action: "fetchGithubOrganizations",
        organizationId
      });

      dispatch(
        projectCreationActions.setGitProviderError({
          organizationId,
          error
        })
      );

      return rejectWithValue({ error });
    }
  },
  {
    condition: ({ organizationId, force = false }, { getState }) => {
      const state = getState();

      // An in-flight request is never worth duplicating, forced or not. This is
      // the clause that makes the concurrent mounts safe: the first dispatch
      // sets `isLoading` synchronously, so the others see it in the same tick.
      if (selectGitProviderLoading(state, { organizationId })) return false;

      if (force) return true;

      // A failure is surfaced with a retry control rather than retried on every
      // render, and a success is fetched once per organization.
      if (selectGitProviderError(state, { organizationId })) return false;

      return !selectHasFetchedGithubOrganizations(state, { organizationId });
    }
  }
);
